import React, { useCallback, useEffect, useState } from "react";
import { Download, Search, UserCheck, UserPlus } from "lucide-react";
import { api } from "@/lib/api";
import { downloadCSV, fdate, money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { EmptyState, LoadingRows } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { UserAvatar } from "@/components/common/UserAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LeadDetail } from "./crm/LeadDetail";
import { CustomerForm } from "./CustomerForm";
import { toast } from "sonner";

export default function Customers() {
  const { can, isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customers", { params: { q } });
      setRows(data.items);
    } finally { setLoading(false); }
  }, [q]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const columns = [
    {
      key: "name", label: "Customer",
      render: (r) => (
        <div className="flex items-center gap-3">
          <UserAvatar name={r.customer_name} size="sm" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{r.customer_name}</p>
            <p className="truncate text-xs text-muted-foreground">{r.company}</p>
          </div>
        </div>
      ),
    },
    { key: "customer_no", label: "Customer ID", render: (r) => <span className="num text-xs text-muted-foreground">{r.customer_no || r.lead_no}</span>, hideMobile: true },
    { key: "phone", label: "Contact", render: (r) => <span className="text-sm">{r.phone}</span> },
    { key: "city", label: "Location", render: (r) => <span className="text-sm text-muted-foreground">{[r.city, r.state].filter(Boolean).join(", ") || "\u2014"}</span>, hideMobile: true },
    ...(isAdmin ? [{ key: "owner", label: "Owner", render: (r) => <span className="text-sm">{r.assigned_to_name}</span> }] : []),
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "invoice_total", label: "Billed", align: "right", render: (r) => <span className="num text-sm font-semibold">{money(r.invoice_total || 0)}</span> },
    { key: "since", label: "Customer since", align: "right", render: (r) => <span className="text-xs text-muted-foreground">{fdate(r.customer_since || r.updated_at)}</span>, hideMobile: true },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        icon={UserCheck}
        title="Customers"
        subtitle="Leads automatically move here once their first invoice is created."
        actions={(
          <div className="flex flex-wrap gap-2">
            {can("customers", "export") && rows.length > 0 && (
              <Button variant="outline" onClick={() => {
                downloadCSV(rows.map((r) => ({
                  "Customer ID": r.customer_no || r.lead_no, Name: r.customer_name, Company: r.company,
                  Phone: r.phone, Email: r.email, City: r.city, Owner: r.assigned_to_name, Billed: r.invoice_total,
                })), "customers.csv");
                toast.success("Customers exported");
              }}>
                <Download className="h-4 w-4" /> Export
              </Button>
            )}
            {can("customers", "add") && (
              <Button onClick={() => setFormOpen(true)} data-testid="new-customer-btn">
                <UserPlus className="h-4 w-4" /> Add customer
              </Button>
            )}
          </div>
        )}
      />

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers" className="pl-9" data-testid="customer-search" />
      </div>

      {loading ? <LoadingRows /> : (
        <DataTable
          columns={columns}
          rows={rows}
          onRowClick={(r) => setDetailId(r.id)}
          empty={<EmptyState icon={UserCheck} title="No customers yet"
            description="As soon as you raise an invoice against a lead, that lead appears here as a customer with its full history." />}
        />
      )}

      <LeadDetail leadId={detailId} open={!!detailId} onOpenChange={(v) => !v && setDetailId(null)} onChanged={load} />
      <CustomerForm open={formOpen} onOpenChange={setFormOpen} onSaved={load} />
    </div>
  );
}
